import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';

import DatePicker from 'components/ui/DatePicker';
import Label from 'components/ui/Label';
import { ActionButton } from 'components/ui/Button';

import { ErrorField, Field, MandatoryFieldLabel, StyledForm, StyledInput, StyledSubmitField, StyledRow } from './SearchBooking.styles';

const referenceRegex = new RegExp('^[a-zA-Z0-9 ]+$');

const SearchPerTrain = ({ onSubmitSearch }) => {
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [errorDatePicker, setErrorDatePicker] = useState('');

  const { register, handleSubmit, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      trainNumber: '',
      reference: '',
      coach: '',
      seat: ''
    }
  });

  const { errors } = formState;

  const hasErrors = Boolean(Object.keys(errors).length > 0);

  const setTravelDate = (_, value) => {
    setDatePickerValue(value);
  };

  const clearTravelDate = () => {
    setDatePickerValue(null);
  };

  const trainNumberHandlers = {
    onKeyDown: (event) => {
      if (event?.keyCode === 69) {
        event?.preventDefault();
      }
    },
    onInput: ({ target }) => {
      if (target?.value?.length > 10) {
        target.value = target.value.slice(0, 10);
      }
    }
  };

  const handleReferenceKeyDown = (event) => {
    if (event?.key) {
      if (referenceRegex.test(event?.key)) {
        return true;
      }

      event?.preventDefault();
      return false;
    }
  };

  return (
    <StyledForm
      data-testid="per-train-form"
      onSubmit={handleSubmit((props) => onSubmitSearch({ ...props, departureDate: datePickerValue }))}
    >
      <Field>
        <MandatoryFieldLabel htmlFor="departureDate">
          <FormattedMessage id="searchBooking.departureDate" />
        </MandatoryFieldLabel>
        <DatePicker
          id="departureDate"
          name="departureDate"
          clearTravelDate={clearTravelDate}
          setTravelDate={setTravelDate}
          value={datePickerValue}
          errorMessage={errorDatePicker}
          setErrorMessage={setErrorDatePicker}
        />
      </Field>
      <Field>
        <MandatoryFieldLabel htmlFor="trainNumber">
          <FormattedMessage id="searchBooking.trainNumber" />
        </MandatoryFieldLabel>
        <StyledInput
          {...register('trainNumber', { required: true, min: 1 })}
          id="trainNumber"
          type="number"
          data-cy="train-number-input"
          data-testid="train-number-input"
          {...trainNumberHandlers}
        />
        {errors.trainNumber && (
          <ErrorField id="error-trainNumber" data-testid="error-trainNumber">
            <FormattedMessage id="searchBooking.trainNumberValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <StyledRow>
        <Field>
          <Label htmlFor="coach">
            <FormattedMessage id="searchBooking.coach" />
          </Label>
          <StyledInput
            {...register('coach', { required: false, min: 1, max: 999 })}
            type="number"
            id="coach"
            data-cy="coach-input"
            data-testid="coach-input"
          />
          {errors.coach && (
            <ErrorField id="error-coach" data-testid="error-coach">
              <FormattedMessage id="searchBooking.coachAndSeatValidationMessage" values={{ number: 999 }} />
            </ErrorField>
          )}
        </Field>
        <Field>
          <Label htmlFor="seat">
            <FormattedMessage id="searchBooking.seat" />
          </Label>
          <StyledInput
            {...register('seat', { required: false, min: 1, max: 999 })}
            type="number"
            id="seat"
            data-cy="seat-input"
            data-testid="seat-input"
          />
          {errors.seat && (
            <ErrorField id="error-seat" data-testid="error-seat">
              <FormattedMessage id="searchBooking.coachAndSeatValidationMessage" values={{ number: 999 }} />
            </ErrorField>
          )}
        </Field>
      </StyledRow>
      <Field>
        <Label htmlFor="reference">
          <FormattedMessage id="searchBooking.reference" />
        </Label>
        <StyledInput
          {...register('reference', { required: false, minLength: 5, maxLength: 25 })}
          type="text"
          id="reference"
          data-cy="reference-input"
          data-testid="reference-input"
          maxLength="25"
          onKeyDown={handleReferenceKeyDown}
        />
        {errors.reference && (
          <ErrorField id="error-reference" data-testid="error-reference">
            <FormattedMessage id="searchBooking.externalReferenceValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <StyledSubmitField>
        <ActionButton data-cy="search-per-train" data-testid="search-per-train" type="submit" disabled={!datePickerValue || hasErrors}>
          <FormattedMessage id="searchBooking.search" />
        </ActionButton>
      </StyledSubmitField>
    </StyledForm>
  );
};

SearchPerTrain.propTypes = {
  onSubmitSearch: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default SearchPerTrain;
